import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { api } from '@/lib/api-client';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { useHeaderAction } from '@/lib/header-action-context';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@/components/ui/select';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
   Collapsible,
   CollapsibleContent,
   CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
   Package,
   Plus,
   Pencil,
   Trash2,
   ChevronDown,
   Layers,
   X,
   Loader2,
   Archive,
} from 'lucide-react';
import AddFeatureDrawer from '@/components/marketplace/AddFeatureDrawer';
import { getCapabilityColors } from '@/lib/capability-colors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Product {
   id: string;
   application_id: string;
   title: string;
   summary?: string;
   description?: string;
   listing_status: string;
   trial_days?: number;
   stripe_product_id?: string;
   created?: string;
}

interface FeaturePrice {
   id: string;
   product_id: string;
   capability_id?: string;
   capability_key?: string;
   capability_type?: string;
   capability_name?: string;
   capability_description?: string;
   name?: string;
   description?: string;
   tier_name?: string;
   pricing_model: string;
   amount_cents?: number;
   currency: string;
   billing_interval: string;
   usage_limit?: number;
   overage_unit_amount_cents?: number;
   metric?: string;
   is_base_price: boolean;
   stripe_price_id?: string;
   stripe_feature_id?: string;
   status: string;
}

interface FeatureFormState {
   capability_key: string;
   tier_name: string;
   pricing_model: string;
   amount_cents: string;
   currency: string;
   billing_interval: string;
   usage_limit: string;
   overage_unit_amount_cents: string;
}

const INITIAL_FEATURE_FORM: FeatureFormState = {
   capability_key: '',
   tier_name: '',
   pricing_model: 'flat',
   amount_cents: '',
   currency: 'usd',
   billing_interval: 'month',
   usage_limit: '',
   overage_unit_amount_cents: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LOG_PREFIX = 'MarketplaceProductDetailPage';

function formatCents(cents?: number): string {
   if (cents == null) return '—';
   return `$${(cents / 100).toFixed(2)}`;
}

function pricingLabel(fp: FeaturePrice): string {
   switch (fp.pricing_model) {
      case 'flat':
         return `${formatCents(fp.amount_cents)}/${fp.billing_interval}`;
      case 'metered':
         return `${formatCents(fp.amount_cents)}/invocation`;
      case 'per_unit':
         return `${formatCents(fp.amount_cents)}/unit/${fp.billing_interval}`;
      default:
         return formatCents(fp.amount_cents);
   }
}

function pricingModelBadgeVariant(model: string): string {
   switch (model) {
      case 'flat':
         return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'metered':
         return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'per_unit':
         return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
         return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200';
   }
}

function statusBadgeVariant(status: string): string {
   switch (status) {
      case 'published':
      case 'active':
         return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      case 'draft':
         return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200';
      case 'archived':
         return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
         return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
   }
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FeaturePriceFormFields({
   form,
   onChange,
}: {
   form: FeatureFormState;
   onChange: (patch: Partial<FeatureFormState>) => void;
}) {
   return (
      <div className="space-y-4">
         <div>
            <Label>Pricing Model</Label>
            <Select
               value={form.pricing_model}
               onValueChange={(v) => onChange({ pricing_model: v })}
            >
               <SelectTrigger>
                  <SelectValue />
               </SelectTrigger>
               <SelectContent>
                  <SelectItem value="flat">Flat</SelectItem>
                  <SelectItem value="metered">Metered</SelectItem>
                  <SelectItem value="per_unit">Per Unit</SelectItem>
               </SelectContent>
            </Select>
         </div>
         <div>
            <Label>Amount (cents)</Label>
            <Input
               type="number"
               value={form.amount_cents}
               onChange={(e) => onChange({ amount_cents: e.target.value })}
               placeholder="e.g. 1000 = $10.00"
            />
         </div>
         <div className="grid grid-cols-2 gap-4">
            <div>
               <Label>Currency</Label>
               <Select
                  value={form.currency}
                  onValueChange={(v) => onChange({ currency: v })}
               >
                  <SelectTrigger>
                     <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                     <SelectItem value="usd">USD</SelectItem>
                     <SelectItem value="eur">EUR</SelectItem>
                     <SelectItem value="gbp">GBP</SelectItem>
                  </SelectContent>
               </Select>
            </div>
            <div>
               <Label>Billing Interval</Label>
               <Select
                  value={form.billing_interval}
                  onValueChange={(v) => onChange({ billing_interval: v })}
               >
                  <SelectTrigger>
                     <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                     <SelectItem value="month">Monthly</SelectItem>
                     <SelectItem value="year">Yearly</SelectItem>
                  </SelectContent>
               </Select>
            </div>
         </div>
         {(form.pricing_model === 'metered' || form.pricing_model === 'per_unit') && (
            <div className="grid grid-cols-2 gap-4">
               <div>
                  <Label>Usage Limit</Label>
                  <Input
                     type="number"
                     value={form.usage_limit}
                     onChange={(e) => onChange({ usage_limit: e.target.value })}
                     placeholder="Optional"
                  />
               </div>
               <div>
                  <Label>Overage Rate (cents)</Label>
                  <Input
                     type="number"
                     value={form.overage_unit_amount_cents}
                     onChange={(e) =>
                        onChange({ overage_unit_amount_cents: e.target.value })
                     }
                     placeholder="Optional"
                  />
               </div>
            </div>
         )}
      </div>
   );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function MarketplaceProductDetailPage() {
   const { productId } = useParams<{ productId: string }>();
   const { setHeaderAction, setHeaderTitle } = useHeaderAction();

   const [product, setProduct] = useState<Product | null>(null);
   const [features, setFeatures] = useState<FeaturePrice[]>([]);
   const [loading, setLoading] = useState(true);
   const [stripeOpen, setStripeOpen] = useState(false);
   const [syncingFeatures, setSyncingFeatures] = useState(false);

   // Status edit
   const [updatingStatus, setUpdatingStatus] = useState(false);

   // Add feature drawer
   const [showAddFeature, setShowAddFeature] = useState(false);

   // Edit feature drawer
   const [editFeature, setEditFeature] = useState<FeaturePrice | null>(null);
   const [editForm, setEditForm] = useState<FeatureFormState>(INITIAL_FEATURE_FORM);
   const [editSubmitting, setEditSubmitting] = useState(false);

   // Delete confirmation
   const [deleteTarget, setDeleteTarget] = useState<FeaturePrice | null>(null);
   const [deleting, setDeleting] = useState(false);

   // Archive confirmation
   const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

   // -----------------------------------------------------------------------
   // Data fetching
   // -----------------------------------------------------------------------

   const fetchProduct = useCallback(async () => {
      if (!productId) return;
      try {
         const res = await api.get(`/billing/products/${productId}`);
         const data = res.data as { product?: Product; prices?: FeaturePrice[] } & Product;
         setProduct(data.product ?? data);
      } catch (err) {
         console.error(`${LOG_PREFIX}.fetchProduct: failed`, err);
         toast.error('Failed to load product');
      }
   }, [productId]);

   const fetchFeatures = useCallback(async () => {
      if (!productId) return;
      try {
         const res = await api.get(`/billing/products/${productId}/features`);
         const data = res.data;
         setFeatures(data?.features ?? (Array.isArray(data) ? data : []));
      } catch (err) {
         console.error(`${LOG_PREFIX}.fetchFeatures: failed`, err);
      }
   }, [productId]);

   const handleSyncStripeFeatures = useCallback(async () => {
      if (!productId) return;
      setSyncingFeatures(true);
      try {
         const res = await api.post(`/billing/products/${productId}/features/sync`);
         const data = res.data as {
            features?: FeaturePrice[];
            synced?: number;
            attached?: number;
            errors?: string[];
         };
         if (Array.isArray(data?.features)) {
            setFeatures(data.features);
         } else {
            await fetchFeatures();
         }
         const errCount = data.errors?.length ?? 0;
         if (errCount > 0) {
            toast.error(`Synced with ${errCount} error(s). Check billing logs.`);
         } else {
            toast.success(
               `Stripe features synced (${data.synced ?? 0} updated, ${data.attached ?? 0} attached)`,
            );
         }
      } catch (err) {
         console.error(`${LOG_PREFIX}.handleSyncStripeFeatures: failed`, err);
         toast.error('Failed to sync Stripe features');
      } finally {
         setSyncingFeatures(false);
      }
   }, [productId, fetchFeatures]);

   useEffect(() => {
      const load = async () => {
         setLoading(true);
         await Promise.all([fetchProduct(), fetchFeatures()]);
         setLoading(false);
      };
      load();
   }, [fetchProduct, fetchFeatures]);

   useEffect(() => {
      if (product) {
         setHeaderTitle({
            icon: (
               <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-100">
                  <Package className="h-4 w-4 text-violet-600" />
               </div>
            ),
            title: product.title,
         });
      }
      return () => setHeaderTitle(null);
   }, [product, setHeaderTitle]);

   useEffect(() => {
      if (product) {
         setHeaderAction(
            <div className="flex items-center gap-2">
               <Button size="sm" onClick={() => setShowAddFeature(true)}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Feature
               </Button>
               {product.listing_status !== 'archived' && (
                  <Button
                     size="sm"
                     variant="outline"
                     onClick={() => setArchiveDialogOpen(true)}
                  >
                     <Archive className="h-4 w-4 mr-1.5" />
                     Archive
                  </Button>
               )}
            </div>
         );
      }
      return () => setHeaderAction(null);
   }, [product, setHeaderAction]);

   // -----------------------------------------------------------------------
   // Actions
   // -----------------------------------------------------------------------

   const handleStatusChange = async (newStatus: string) => {
      if (!productId) return;
      setUpdatingStatus(true);
      try {
         await api.put(`/billing/products/${productId}`, { listing_status: newStatus });
         setProduct((prev) => (prev ? { ...prev, listing_status: newStatus } : prev));
         toast.success(`Product status updated to ${newStatus}`);
      } catch (err) {
         console.error(`${LOG_PREFIX}.handleStatusChange: failed`, err);
         toast.error('Failed to update product status');
      } finally {
         setUpdatingStatus(false);
      }
   };

   const openEditDrawer = (fp: FeaturePrice) => {
      setEditFeature(fp);
      setEditForm({
         capability_key: fp.capability_key ?? '',
         tier_name: fp.tier_name ?? '',
         pricing_model: fp.pricing_model,
         amount_cents: fp.amount_cents != null ? String(fp.amount_cents) : '',
         currency: fp.currency,
         billing_interval: fp.billing_interval,
         usage_limit: fp.usage_limit != null ? String(fp.usage_limit) : '',
         overage_unit_amount_cents:
            fp.overage_unit_amount_cents != null
               ? String(fp.overage_unit_amount_cents)
               : '',
      });
   };

   const closeEditDrawer = () => setEditFeature(null);

   const handleEditFeature = async () => {
      if (!productId || !editFeature) return;
      setEditSubmitting(true);
      try {
         await api.put(`/billing/products/${productId}/features/${editFeature.id}`, {
            pricing_model: editForm.pricing_model,
            amount_cents: editForm.amount_cents ? parseInt(editForm.amount_cents) : undefined,
            currency: editForm.currency,
            billing_interval: editForm.billing_interval,
            usage_limit: editForm.usage_limit ? parseInt(editForm.usage_limit) : undefined,
            overage_unit_amount_cents: editForm.overage_unit_amount_cents
               ? parseInt(editForm.overage_unit_amount_cents)
               : undefined,
         });
         toast.success('Feature updated');
         closeEditDrawer();
         await fetchFeatures();
      } catch (err) {
         console.error(`${LOG_PREFIX}.handleEditFeature: failed`, err);
         toast.error('Failed to update feature');
      } finally {
         setEditSubmitting(false);
      }
   };

   const handleDeleteFeature = async () => {
      if (!productId || !deleteTarget) return;
      setDeleting(true);
      try {
         await api.delete(`/billing/products/${productId}/features/${deleteTarget.id}`);
         toast.success('Feature removed');
         setDeleteTarget(null);
         await fetchFeatures();
      } catch (err) {
         console.error(`${LOG_PREFIX}.handleDeleteFeature: failed`, err);
         toast.error('Failed to remove feature');
      } finally {
         setDeleting(false);
      }
   };

   const handleArchiveProduct = async () => {
      if (!productId) return;
      setUpdatingStatus(true);
      try {
         await api.put(`/billing/products/${productId}`, { listing_status: 'archived' });
         setProduct((prev) => (prev ? { ...prev, listing_status: 'archived' } : prev));
         setArchiveDialogOpen(false);
         toast.success('Product archived');
      } catch (err) {
         console.error(`${LOG_PREFIX}.handleArchiveProduct: failed`, err);
         toast.error('Failed to archive product');
      } finally {
         setUpdatingStatus(false);
      }
   };

   // -----------------------------------------------------------------------
   // Derived data
   // -----------------------------------------------------------------------

   const basePrice = features.find((f) => f.is_base_price);
   const featurePrices = features.filter((f) => !f.is_base_price);
   const missingStripeFeatures =
      !!product?.stripe_product_id &&
      features.length > 0 &&
      features.some((f) => !f.stripe_feature_id);

   const existingFeatureCapabilityIds = useMemo(
      () => new Set(features.map((f) => f.capability_id).filter((id): id is string => id != null)),
      [features],
   );

   // -----------------------------------------------------------------------
   // Render
   // -----------------------------------------------------------------------

   if (loading) {
      return (
         <div className="flex items-center justify-center p-12">
            <div className="text-muted-foreground">Loading product details...</div>
         </div>
      );
   }

   if (!product) {
      return (
         <div className="flex flex-col items-center justify-center p-12 gap-2">
            <Package className="h-10 w-10 text-muted-foreground" />
            <p className="text-destructive font-medium">Product not found</p>
         </div>
      );
   }

   return (
      <div className="p-6 space-y-8 max-w-6xl mx-auto">
         {/* ---- Header ---- */}
         <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="space-y-1">
               <h2 className="text-2xl font-semibold tracking-tight">
                  Capability Features
               </h2>
               <p className="text-sm text-muted-foreground">
                  Individual capability pricing attached to this product
               </p>
               {product.summary && (
                  <p className="text-muted-foreground">{product.summary}</p>
               )}
               {product.description && (
                  <p className="text-sm text-muted-foreground">{product.description}</p>
               )}
               {product.trial_days != null && product.trial_days > 0 && (
                  <p className="text-sm text-muted-foreground">
                     {product.trial_days}-day free trial included
                  </p>
               )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
               <Select
                  value={product.listing_status}
                  onValueChange={handleStatusChange}
                  disabled={updatingStatus}
               >
                  <SelectTrigger className="w-36">
                     <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                     <SelectItem value="draft">Draft</SelectItem>
                     <SelectItem value="published">Published</SelectItem>
                     <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
               </Select>
            </div>
         </div>

         {/* ---- Base Price Card ---- */}
         {basePrice && (
            <Card className="border border-slate-200 bg-white shadow-sm">
               <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                     <CardTitle className="text-lg">Base Price</CardTitle>
                     <CardDescription>
                        The recurring subscription price for this product
                     </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                     <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDrawer(basePrice)}
                     >
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit
                     </Button>
                  </div>
               </CardHeader>
               <CardContent>
                  <div className="flex items-baseline gap-1">
                     <span className="text-4xl font-bold tracking-tight">
                        {formatCents(basePrice.amount_cents)}
                     </span>
                     <span className="text-muted-foreground text-lg">
                        /{basePrice.billing_interval}
                     </span>
                  </div>
                  {basePrice.usage_limit != null && (
                     <p className="text-sm text-muted-foreground mt-1">
                        Up to {basePrice.usage_limit.toLocaleString()} invocations included
                     </p>
                  )}
               </CardContent>
            </Card>
         )}

         {/* ---- Feature Prices Section ---- */}
         <div>
            <h2 className="font-semibold text-slate-900 mb-4">Features</h2>
            {featurePrices.length === 0 ? (
               <Card className="border border-slate-200 bg-white shadow-sm">
                  <CardContent className="py-12 text-center">
                     <Layers className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                     <p className="text-muted-foreground">
                        No capability features configured yet.
                     </p>
                     <p className="text-sm text-muted-foreground mt-1">
                        Add features to define per-capability pricing.
                     </p>
                  </CardContent>
               </Card>
            ) : (
               <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {featurePrices.map((fp) => {
                     const capType = fp.capability_type ?? 'app';
                     const colors = getCapabilityColors(capType);
                     const Icon = colors.icon;
                     const capDescription = fp.capability_description || fp.description || null;
                     const metricLabel = fp.metric
                        ? fp.metric.replace(/_/g, ' ')
                        : null;
                     return (
                        <Card
                           key={fp.id}
                           className={`relative cursor-pointer p-5 min-h-30 border-l-4 ${colors.border} ${colors.hover} transition-colors`}
                           onClick={() => openEditDrawer(fp)}
                        >
                           <div className="flex items-center gap-3">
                              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${colors.bgLight} ${colors.text}`}>
                                 <Icon size={20} className={colors.text} />
                              </div>
                              <div className="min-w-0">
                                 <p className="font-medium text-slate-900 truncate">
                                    {fp.name || fp.capability_name || fp.capability_key || 'Unknown'}
                                 </p>
                                 {capDescription && (
                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                       {capDescription}
                                    </p>
                                 )}
                                 {fp.tier_name && (
                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                       Tier: {fp.tier_name}
                                    </p>
                                 )}
                              </div>
                           </div>
                           <div className="mt-3 space-y-1">
                              <div className="flex items-baseline gap-2">
                                 <span className="text-lg font-semibold text-slate-900">
                                    {pricingLabel(fp)}
                                 </span>
                                 <Badge
                                    className={`text-[10px] capitalize ${pricingModelBadgeVariant(fp.pricing_model)}`}
                                 >
                                    {fp.pricing_model.replace(/_/g, ' ')}
                                 </Badge>
                              </div>
                              <div className="space-y-0.5">
                                 {fp.pricing_model === 'metered' && (
                                    <>
                                       {metricLabel && (
                                          <p className="text-xs text-slate-500">
                                             Billed per {metricLabel}
                                          </p>
                                       )}
                                       {fp.usage_limit != null && (
                                          <p className="text-xs text-slate-500">
                                             {fp.usage_limit.toLocaleString()} {metricLabel ?? 'units'} included
                                          </p>
                                       )}
                                       {fp.overage_unit_amount_cents != null && fp.overage_unit_amount_cents > 0 && (
                                          <p className="text-xs text-slate-500">
                                             Overage: {formatCents(fp.overage_unit_amount_cents)}/{metricLabel ? metricLabel.replace(/s$/, '') : 'unit'}
                                          </p>
                                       )}
                                    </>
                                 )}
                                 {fp.pricing_model === 'per_unit' && (
                                    <>
                                       {fp.usage_limit != null && (
                                          <p className="text-xs text-slate-500">
                                             Up to {fp.usage_limit.toLocaleString()} units/{fp.billing_interval}
                                          </p>
                                       )}
                                       {fp.overage_unit_amount_cents != null && fp.overage_unit_amount_cents > 0 && (
                                          <p className="text-xs text-slate-500">
                                             Overage: {formatCents(fp.overage_unit_amount_cents)}/unit
                                          </p>
                                       )}
                                    </>
                                 )}
                                 {fp.pricing_model === 'flat' && fp.billing_interval && (
                                    <p className="text-xs text-slate-500">
                                       Billed {fp.billing_interval === 'year' ? 'annually' : 'monthly'}
                                    </p>
                                 )}
                              </div>
                           </div>
                        </Card>
                     );
                  })}
               </div>
            )}
         </div>

         {/* ---- Stripe IDs (collapsible) ---- */}
         <Collapsible open={stripeOpen} onOpenChange={setStripeOpen}>
            <Card className="border border-slate-200 bg-white shadow-sm">
               <CollapsibleTrigger asChild>
                  <button
                     type="button"
                     className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-muted/50 transition-colors rounded-xl"
                  >
                     <span className="text-sm font-medium">Stripe Integration Details</span>
                     <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform ${stripeOpen ? 'rotate-180' : ''
                           }`}
                     />
                  </button>
               </CollapsibleTrigger>
               <CollapsibleContent>
                  <CardContent className="space-y-3 pt-0">
                     {missingStripeFeatures && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                           <p className="mb-2">
                              Feature prices exist locally but Stripe feature IDs are missing.
                              Sync to create/attach entitlement features on the Stripe product.
                           </p>
                           <Button
                              size="sm"
                              variant="outline"
                              disabled={syncingFeatures}
                              onClick={handleSyncStripeFeatures}
                           >
                              {syncingFeatures ? (
                                 <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                              ) : null}
                              Sync Stripe Features
                           </Button>
                        </div>
                     )}
                     <div>
                        <Label className="text-xs text-muted-foreground">
                           Stripe Product ID
                        </Label>
                        <p className="text-sm font-mono">
                           {product.stripe_product_id || '—'}
                        </p>
                     </div>
                     {features.length > 0 && (
                        <>
                           <div>
                              <Label className="text-xs text-muted-foreground">
                                 Stripe Feature IDs
                              </Label>
                              <div className="space-y-1 mt-1">
                                 {features
                                    .filter((f) => f.stripe_feature_id)
                                    .map((f) => (
                                       <p key={f.id} className="text-xs font-mono text-muted-foreground">
                                          {f.name || f.capability_name || f.capability_key || 'base'}:{' '}
                                          {f.stripe_feature_id}
                                       </p>
                                    ))}
                                 {features.every((f) => !f.stripe_feature_id) && (
                                    <p className="text-xs text-muted-foreground">None</p>
                                 )}
                              </div>
                           </div>
                           <div>
                              <Label className="text-xs text-muted-foreground">
                                 Stripe Price IDs
                              </Label>
                              <div className="space-y-1 mt-1">
                                 {features
                                    .filter((f) => f.stripe_price_id)
                                    .map((f) => (
                                       <p key={f.id} className="text-xs font-mono text-muted-foreground">
                                          {f.name || f.capability_name || f.capability_key || 'base'}:{' '}
                                          {f.stripe_price_id}
                                       </p>
                                    ))}
                                 {features.every((f) => !f.stripe_price_id) && (
                                    <p className="text-xs text-muted-foreground">None</p>
                                 )}
                              </div>
                           </div>
                        </>
                     )}
                  </CardContent>
               </CollapsibleContent>
            </Card>
         </Collapsible>

         {/* ---- Add Feature Drawer ---- */}
         {product && (
            <AddFeatureDrawer
               isOpen={showAddFeature}
               onClose={() => setShowAddFeature(false)}
               onFeatureAdded={fetchFeatures}
               productId={productId!}
               applicationId={product.application_id}
               existingFeatureCapabilityIds={existingFeatureCapabilityIds}
            />
         )}

         {/* ---- Edit Feature Drawer ---- */}
         <SlideDrawer
            isOpen={editFeature !== null}
            onClose={closeEditDrawer}
         >
            {editFeature && (
               <>
                  {/* Drawer header */}
                  <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50 shrink-0">
                     <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                           {(() => {
                              const ct = editFeature.capability_type ?? 'app';
                              const ec = getCapabilityColors(ct);
                              const EIcon = ec.icon;
                              return (
                                 <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${ec.bgLight} ${ec.text}`}>
                                    <EIcon size={20} className={ec.text} />
                                 </div>
                              );
                           })()}
                           <div>
                              <h2 className="text-xl font-semibold text-slate-900">
                                 {editFeature.name || editFeature.capability_name || editFeature.capability_key || 'Edit Feature'}
                              </h2>
                              <p className="text-sm text-slate-500">
                                 {editFeature.capability_description || editFeature.description || 'Update pricing for this feature'}
                              </p>
                           </div>
                        </div>
                        <button
                           type="button"
                           onClick={closeEditDrawer}
                           className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                           aria-label="Close"
                        >
                           <X size={20} />
                        </button>
                     </div>
                  </div>

                  {/* Drawer content */}
                  <div className="flex-1 overflow-y-auto p-8 bg-white">
                     <div className="max-w-xl mx-auto">
                        <FeaturePriceFormFields
                           form={editForm}
                           onChange={(patch) => setEditForm((prev) => ({ ...prev, ...patch }))}
                        />
                     </div>
                  </div>

                  {/* Drawer footer */}
                  <div className="px-8 pt-4 pb-8 border-t border-slate-100 bg-slate-50/50 shrink-0">
                     <div className="flex items-center justify-between max-w-xl mx-auto">
                        <Button
                           variant="ghost"
                           size="sm"
                           className="text-destructive hover:text-destructive hover:bg-destructive/10"
                           onClick={() => { setDeleteTarget(editFeature); closeEditDrawer(); }}
                        >
                           <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                           Remove
                        </Button>
                        <div className="flex gap-3">
                           <Button variant="outline" onClick={closeEditDrawer}>
                              Cancel
                           </Button>
                           <Button onClick={handleEditFeature} disabled={editSubmitting}>
                              {editSubmitting ? (
                                 <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                 </>
                              ) : (
                                 'Save Changes'
                              )}
                           </Button>
                        </div>
                     </div>
                  </div>
               </>
            )}
         </SlideDrawer>

         {/* ---- Archive Confirmation ---- */}
         <AlertDialog
            open={archiveDialogOpen}
            onOpenChange={setArchiveDialogOpen}
         >
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Archive Product</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to archive{' '}
                     <strong>{product?.title}</strong>? The product will be set to
                     Archived status and will no longer be available for new subscriptions.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel disabled={updatingStatus}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleArchiveProduct}
                     disabled={updatingStatus}
                  >
                     {updatingStatus ? (
                        <>
                           <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                           Archiving...
                        </>
                     ) : (
                        'Archive'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         {/* ---- Delete Confirmation ---- */}
         <AlertDialog
            open={deleteTarget !== null}
            onOpenChange={(open) => !open && setDeleteTarget(null)}
         >
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Remove Feature</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to remove{' '}
                     <strong>
                        {deleteTarget?.name ||
                           deleteTarget?.capability_name ||
                           deleteTarget?.capability_key ||
                           'this feature'}
                     </strong>{' '}
                     from this product? This will archive the associated pricing.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleDeleteFeature}
                     disabled={deleting}
                     className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                     {deleting ? 'Removing...' : 'Remove'}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
}
